import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  message,
} from 'antd';
import {
  PlusOutlined,
  BarChartOutlined,
  ApartmentOutlined,
  EyeOutlined,
} from '@ant-design/icons';

// Import organized components
import MuscleGroupTree from './components/MuscleGroupTree';
import MuscleGroupForm from './components/MuscleGroupForm';
import StatsCard from '../components/shared/StatsCard';

// Import data and utilities
import { mockMuscleGroups, mockCategoryDistribution } from './data/mockData';
import { calculateMuscleGroupStats } from './utils/muscleGroupUtils';
import { MuscleGroup } from '../types';
import { apiService } from '../../../services/api';

const { Title } = Typography;

const MuscleGroupsLibrary: React.FC = () => {
  const [muscleGroups, setMuscleGroups] = useState<MuscleGroup[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMuscleGroup, setEditingMuscleGroup] = useState<MuscleGroup | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [form] = Form.useForm();

  // Load data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setInitialLoading(true);
        
        const response = await apiService.getMuscleGroups();

        if (response.success && response.data) {
          setMuscleGroups(response.data);
          // Set default expanded keys based on categories
          const categories = Array.from(new Set(response.data.map(mg => mg.category).filter(Boolean))) as string[];
          setExpandedKeys(categories);
        } else {
          console.warn('Failed to load muscle groups, using mock data');
          setMuscleGroups(mockMuscleGroups);
          setExpandedKeys(['Upper Body', 'Lower Body', 'Core']);
        }

      } catch (error) {
        console.error('Error loading muscle groups:', error);
        message.error('Failed to load muscle groups. Using cached data.');
        // Fallback to mock data on error
        setMuscleGroups(mockMuscleGroups);
        setExpandedKeys(['Upper Body', 'Lower Body', 'Core']);
      } finally {
        setInitialLoading(false);
      }
    };

    loadData();
  }, []);

  const handleAdd = () => {
    setEditingMuscleGroup(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (muscleGroup: MuscleGroup) => {
    setEditingMuscleGroup(muscleGroup);
    form.setFieldsValue(muscleGroup);
    setIsModalVisible(true);
  };

  const handleDelete = (muscleGroup: MuscleGroup) => {
    Modal.confirm({
      title: 'Delete Muscle Group',
      content: `Are you sure you want to delete "${muscleGroup.name}"?`,
      onOk: async () => {
        try {
          setLoading(true);
          const response = await apiService.deleteMuscleGroup(muscleGroup.id);
          
          if (response.success) {
            setMuscleGroups(prev => prev.filter(mg => mg.id !== muscleGroup.id));
            message.success('Muscle group deleted successfully');
          } else {
            message.error('Failed to delete muscle group');
          }
        } catch (error) {
          console.error('Error deleting muscle group:', error);
          message.error('Failed to delete muscle group');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleSave = async (values: any) => {
    try {
      setLoading(true);
      
      if (editingMuscleGroup) {
        // Update existing muscle group
        const response = await apiService.updateMuscleGroup(editingMuscleGroup.id, values);
        
        if (response.success && response.data) {
          setMuscleGroups(prev => prev.map(mg => 
            mg.id === editingMuscleGroup.id ? response.data! : mg
          ));
          message.success('Muscle group updated successfully');
        } else {
          message.error('Failed to update muscle group');
          return;
        }
      } else {
        // Add new muscle group
        const response = await apiService.createMuscleGroup(values);
        
        if (response.success && response.data) {
          setMuscleGroups(prev => [...prev, response.data!]);
          message.success('Muscle group added successfully');
        } else {
          message.error('Failed to create muscle group');
          return;
        }
      }
      
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error saving muscle group:', error);
      message.error('Failed to save muscle group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Row gutter={[24, 24]}>
        {/* Header */}
        <Col span={24}>
          <Card>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  <ApartmentOutlined /> Muscle Groups Library
                </Title>
                <Typography.Text type="secondary">
                  Anatomical organization and muscle group management
                </Typography.Text>
              </Col>
              <Col>
                <Space>
                  <Button icon={<EyeOutlined />}>
                    Anatomical View
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={handleAdd}
                    loading={loading}
                  >
                    Add Muscle Group
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Statistics */}
        <Col span={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <StatsCard
                title="Overview"
                icon={<BarChartOutlined />}
                stats={calculateMuscleGroupStats(muscleGroups)}
                size="small"
              />
            </Col>
            <Col xs={24} lg={8}>
              <StatsCard
                title="Category Distribution"
                progress={mockCategoryDistribution}
                size="small"
              />
            </Col>
          </Row>
        </Col>

        {/* Muscle Groups Tree */}
        <Col span={24}>
          <Card 
            title="Muscle Groups Hierarchy" 
            size="small"
            loading={initialLoading}
          >
            <MuscleGroupTree
              muscleGroups={muscleGroups}
              selectedKeys={selectedKeys}
              expandedKeys={expandedKeys}
              onSelect={setSelectedKeys}
              onExpand={setExpandedKeys}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Modal */}
      <Modal
        title={editingMuscleGroup ? 'Edit Muscle Group' : 'Add New Muscle Group'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        <MuscleGroupForm
          form={form}
          muscleGroup={editingMuscleGroup}
          onSave={handleSave}
          onCancel={() => setIsModalVisible(false)}
        />
      </Modal>
    </div>
  );
};

export default MuscleGroupsLibrary; 